// The index (CLAUDE.md: Card order on the index, Game descriptions).
//
// The daily is the hero; the always-available games are cards under a fading
// divider. **No How it works links**: the "?" in the site header is the one way
// into How to play, here as on every page (owner, 12 October). The structure says how each game is served, so nothing on this page
// has to: no badges, no "new every day" on a card.
//
// The hero carries no number and no date. The index is where a player chooses a
// game, not where they identify a puzzle: the number belongs on the game screen,
// where it labels the thing in front of them. A number here only asks a question
// nobody arriving has — which one is this? — and answers it with a figure that
// means nothing until they are playing.
import { Link } from 'react-router-dom';
import { NextCountdown } from '../components/NextButton.tsx';
import { useNextPuzzle } from '../lib/next-puzzle.ts';
import type { GameKey } from '../lib/history.ts';
import './Index.css';

/**
 * Play opens this player's own next puzzle in the game (lib/next-puzzle.ts),
 * straight at its address. **It is not shown until that is known** (owner, 16
 * October): its place is held, invisible, so nothing moves when it appears,
 * and it never shows one thing and then switches to another. With every
 * released puzzle finished it becomes the countdown to the next one — or
 * nothing, where no time is known or the time has passed and the puzzle is not
 * there yet. The label stays "Play": the index carries no puzzle number.
 */
function Play({ game, className }: { game: GameKey; className: string }) {
  const next = useNextPuzzle(game);
  if (next?.pick === 'done') return <NextCountdown game={game} at={next.arrival} box={className} />;
  if (!next || next.pick === null) {
    return <span className={`btn btn-primary ${className} play-pending`} aria-hidden="true">Play</span>;
  }
  return <Link className={`btn btn-primary ${className}`} to={`/${game}/${next.pick.number}`}>Play</Link>;
}

const GAMES = [
  {
    slug: 'call-sheet' as GameKey,
    name: 'Call Sheet',
    description: 'Name the movie or show from its cast.'
  }
];

export function Index() {
  return (
    <main className="column index">
      {/* The site in one line, for screen readers and search engines; it is in
          index.html's first draw too, so the page reads the same either side. */}
      <p className="visually-hidden">Cinemore: two film quizzes. Running Order is a new puzzle every day; Call Sheet you can play as often as you like.</p>
      <section className="hero">
        <h1 className="index-title">Running Order</h1>
        <p className="index-description">Five movies. Put them in release order.</p>
        <Play game="running-order" className="btn-block hero-play" />
      </section>

      {/* The divider is deliberate: the card's own border is not a substitute. */}
      <hr className="rule index-divider" />

      <div className="index-cards">
        {GAMES.map((game) => (
          // Title and description stack on the left; Play sits beside them on
          // the right, spanning both rows. Play comes last
          // in the markup and is placed by the grid, so it is read after the
          // text rather than between the title and the description.
          <article className="card index-card" key={game.slug}>
            <h2 className="index-card-title">{game.name}</h2>
            <p className="index-card-description">{game.description}</p>
            <Play game={game.slug} className="card-play" />
          </article>
        ))}
      </div>
    </main>
  );
}
